import { CityFilter, CountryFilter } from 'components/Filter';
import StateFilter from 'components/Filter/StateFilter';
import NeighborhoodFilter from 'components/Filter/NeighborhoodFilter';
import useAddRequestStore from 'store/addRequestStore';
import { Region } from 'types/Region';
import AddRequestHeader from './AddRequestHeader';
import { MALLORCA_NOT_DEFINED_LOCATIONS } from 'util/Constants';
import { FormControlLabel, Checkbox, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useState, useEffect } from 'react';

interface LocationDetails {
    isEditMode?: boolean;
}

export default function LocationDetails({ isEditMode }: LocationDetails) {
    const { requestData, setRequestData, clearErrors, errors, region, setRegion, usePredefinedLocations, setUsePredefinedLocations } = useAddRequestStore();
    const [selectedPredefinedLocation, setSelectedPredefinedLocation] = useState<string>('');

    useEffect(() => {
        if (usePredefinedLocations && requestData.definedArea) {
            setSelectedPredefinedLocation(requestData.definedArea);
        }
    }, [usePredefinedLocations, requestData.definedArea]);

    // Preselect Spain and Balearic Islands
    useEffect(() => {
        if (!usePredefinedLocations) {
            setRegion({ name: 'Spain', citiesAndStates: [], nonCitiesAndStates: [] });
            setRequestData({
                propertyCountry: 'Spain',
                propertyState: ['Balearic Islands'],
                propertyCity: [],
                definedArea: '',
            });
        }
    }, [usePredefinedLocations]);

    const getWidthClass = (isEditMode?: boolean) => (isEditMode ? 'w-full' : 'default-lg:w-2/3 default-xl:w-1/2 default-md:w-2/3 default-sm:w-full');

    const handlePredefinedLocationsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUsePredefinedLocations(event.target.checked);
        if (event.target.checked) {
            setRequestData({
                propertyCountry: '',
                propertyState: [],
                propertyCity: [],
                definedArea: '',
            });
            clearErrors('propertyState');
            clearErrors('propertyCity');
            clearErrors('definedArea');
        } else {
            setRequestData({
                propertyCountry: '',
                propertyState: [],
                propertyCity: [],
                definedArea: '',
            });
            setSelectedPredefinedLocation('');
            clearErrors('propertyState');
            clearErrors('propertyCity');
            clearErrors('definedArea');
        }
    };

    const handlePredefinedLocationChange = (event: any) => {
        const value = event.target.value;
        setSelectedPredefinedLocation(value);
        setRequestData({
            propertyCountry: '',
            propertyState: [],
            propertyCity: [],
            definedArea: value,
        });
        clearErrors('definedArea');
    };

    return (
        <div className="font-poppins">
            <AddRequestHeader title="Choose Your Prefered Location" description="Specify where your buyer is searching" />

            <ItemContainer title="Select this option if you want to search in a specific region" description="" showBorder={false}>
                <div className="flex items-center">
                    <FormControlLabel
                        control={<Checkbox checked={usePredefinedLocations} onChange={handlePredefinedLocationsChange} color="primary" />}
                        label="Define by region"
                    />
                </div>
            </ItemContainer>

            {!usePredefinedLocations ? (
                <>
                    <ItemContainer title="Country" description="Select the main country for your request">
                        <div className={`lg:h-14 h-12 ${getWidthClass(isEditMode)}`}>
                            <CountryFilter
                                selectedRegion={region}
                                onChanged={(region: Region) => {
                                    setRegion(region);
                                    setRequestData({ propertyCountry: region.name, propertyCity: [], propertyState: [], definedArea: '' });
                                    clearErrors('propertyCountry');
                                }}
                            />
                            {errors.propertyCountry && <p className="text-red-500 text-sm !mt-2">{errors.propertyCountry}</p>}
                        </div>
                    </ItemContainer>

                    <ItemContainer title="State" description="Choose the main town or neighborhood">
                        <div className={`lg:h-14 h-12 ${getWidthClass(isEditMode)}`}>
                            <StateFilter
                                region={region}
                                selectedStates={requestData.propertyState}
                                onChanged={(state: string[]) => {
                                    setRequestData({ propertyState: state, propertyCity: [], definedArea: '' });
                                    clearErrors('propertyState');
                                }}
                                placeholder="State"
                            />
                            {errors.propertyState && <p className="text-red-500 text-sm !mt-2">{errors.propertyState}</p>}
                        </div>
                    </ItemContainer>

                    <ItemContainer title="Area" description="Choose the area of interest">
                        <div className={`lg:h-14 h-12 ${getWidthClass(isEditMode)}`}>
                            <CityFilter
                                onChanged={(city: string[]) => {
                                    setRequestData({ propertyCity: city, propertyNeighborhood: [], definedArea: '' });
                                    clearErrors('propertyCity');
                                }}
                                region={region}
                                selectedCities={requestData.propertyCity}
                                placeholder="Area"
                            />
                            {errors.propertyCity && <p className="text-red-500 text-sm !mt-2">{errors.propertyCity}</p>}
                        </div>
                    </ItemContainer>

                    <ItemContainer title="Neighborhood" description="Choose the specific neighborhood">
                        <div className={`lg:h-14 h-12 ${getWidthClass(isEditMode)}`}>
                            <NeighborhoodFilter
                                onChanged={(neighborhood: string[]) => {
                                    setRequestData({ propertyNeighborhood: neighborhood, definedArea: '' });
                                    clearErrors('propertyNeighborhood');
                                }}
                                region={region}
                                selectedNeighborhoods={requestData.propertyNeighborhood}
                                selectedStates={requestData.propertyState}
                                selectedCities={requestData.propertyCity}
                                placeholder="Neighborhood"
                            />
                            {errors.propertyNeighborhood && <p className="text-red-500 text-sm !mt-2">{errors.propertyNeighborhood}</p>}
                        </div>
                    </ItemContainer>
                </>
            ) : (
                <ItemContainer title="Select Area" description="Choose the region where you want to search">
                    <div className={`lg:h-14 h-12 ${getWidthClass(isEditMode)}`}>
                        <FormControl fullWidth>
                            <InputLabel>Select Area</InputLabel>
                            <Select value={selectedPredefinedLocation} onChange={handlePredefinedLocationChange} label="Select Area">
                                {MALLORCA_NOT_DEFINED_LOCATIONS.map((location) => (
                                    <MenuItem key={location} value={location}>
                                        {location}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.definedArea && <p className="text-red-500 text-sm !mt-2">{errors.definedArea}</p>}
                        </FormControl>
                    </div>
                </ItemContainer>
            )}
        </div>
    );
}

interface Props {
    title: string;
    description: string;
    children?: React.ReactNode;
    showBorder?: boolean;
}

function ItemContainer({ title, description, children, showBorder = true }: Props) {
    return (
        <div className="space-y-10 mt-5">
            <div className="space-y-2 lg:space-y-3">
                <label htmlFor="title" className="block text-base font-semibold text-light-black">
                    {title}
                </label>
                <p className="text-[12px] text-light-black/80 mb-2">{description}</p>
                {children}
            </div>
            {showBorder && <div className="border border-my-gray/40" />}
        </div>
    );
}
